import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SlidersHorizontal } from 'lucide-react';
import toast from 'react-hot-toast';

import { notificationsApi } from '../../../api/notifications.js';
import { rolesApi } from '../../../api/roles.js';
import { ApiError } from '../../../api/client.js';
import { Card } from '../../../components/ui/Card.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Switch } from '../../../components/ui/Switch.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Modal } from '../../../components/ui/Modal.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { MultiSelect } from '../../../components/ui/MultiSelect.jsx';
import { TagInput } from '../../../components/ui/TagInput.jsx';
import { t } from '../../../lib/i18n.js';

function RuleModal({ open, onClose, event, templates, roleOptions, onSaved }) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!open || !event) return;
    const r = event.rule || {};
    setForm({
      enabled: r.enabled || false,
      templateKey: r.templateKey || 'default',
      minSeverity: r.minSeverity || '',
      throttleSeconds: r.throttleSeconds ?? 0,
      roles: (r.recipients && r.recipients.roles) || [],
      emails: (r.recipients && r.recipients.emails) || [],
      includeSubject: (r.recipients && r.recipients.includeSubject) || false
    });
  }, [open, event]);

  const mutation = useMutation({
    mutationFn: (body) => notificationsApi.updateRule(event.key, body),
    onSuccess: () => { toast.success('Regola salvata.'); onSaved(); onClose(); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = () => mutation.mutate({
    enabled: form.enabled,
    templateKey: form.templateKey || null,
    minSeverity: form.minSeverity || null,
    throttleSeconds: Number(form.throttleSeconds) || 0,
    recipients: { roles: form.roles, emails: form.emails, includeSubject: form.includeSubject }
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={event ? event.label : 'Regola'}
      description={event ? event.description : ''}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={submit} loading={mutation.isPending}>Salva regola</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between rounded-lg border border-border bg-surface p-3">
          <span className="text-sm font-medium text-ink">Invia notifica per questo evento</span>
          <Switch checked={form.enabled} onChange={(v) => set('enabled', v)} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select label="Template" value={form.templateKey} onChange={(e) => set('templateKey', e.target.value)}>
            {templates.map((tpl) => <option key={tpl.key} value={tpl.key}>{tpl.name}</option>)}
          </Select>
          <Select label="Gravità minima" value={form.minSeverity} onChange={(e) => set('minSeverity', e.target.value)}>
            <option value="">Qualsiasi</option>
            <option value="info">Info</option>
            <option value="notice">Notice</option>
            <option value="warning">Warning</option>
            <option value="critical">Critical</option>
          </Select>
        </div>

        <MultiSelect label="Invia ai ruoli" options={roleOptions} value={form.roles} onChange={(v) => set('roles', v)} placeholder="Seleziona ruoli..." hint="Tutti gli utenti attivi con questi ruoli." />
        <TagInput label="Email aggiuntive" value={form.emails} onChange={(v) => set('emails', v)} lowercase placeholder="email e Invio" />

        <div className="flex items-center justify-between rounded-lg border border-border bg-surface p-3">
          <div>
            <p className="text-sm font-medium text-ink">Notifica anche l'utente coinvolto</p>
            <p className="text-xs text-ink-muted">Es. avvisa il diretto interessato di un account bloccato.</p>
          </div>
          <Switch checked={form.includeSubject} onChange={(v) => set('includeSubject', v)} />
        </div>

        <Input label="Anti-spam (secondi)" type="number" min="0" value={form.throttleSeconds} onChange={(e) => set('throttleSeconds', e.target.value)} hint="Sopprime invii ripetuti dello stesso evento entro questo intervallo. 0 = nessun limite." />
      </div>
    </Modal>
  );
}

export function RulesTab({ canManage }) {
  const queryClient = useQueryClient();
  const eventsQuery = useQuery({ queryKey: ['notifications', 'events'], queryFn: notificationsApi.listEvents });
  const templatesQuery = useQuery({ queryKey: ['notifications', 'templates', 'all'], queryFn: () => notificationsApi.listTemplates({ limit: 100 }) });
  const rolesQuery = useQuery({ queryKey: ['roles', 'all'], queryFn: () => rolesApi.list({ limit: 100 }) });

  const [editing, setEditing] = useState(null);

  const templates = templatesQuery.data?.items || [];
  const roleOptions = useMemo(
    () => (rolesQuery.data?.items || []).map((r) => ({ value: r.name, label: r.label || r.name })),
    [rolesQuery.data]
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['notifications', 'events'] });

  const toggleMutation = useMutation({
    mutationFn: ({ key, enabled }) => notificationsApi.updateRule(key, { enabled }),
    onSuccess: () => invalidate(),
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const categories = eventsQuery.data?.categories || [];
  const byCategory = useMemo(() => {
    const map = {};
    for (const e of (eventsQuery.data?.events || [])) (map[e.category] = map[e.category] || []).push(e);
    return map;
  }, [eventsQuery.data]);

  if (eventsQuery.isLoading) return <Card><p className="text-sm text-ink-muted">Caricamento...</p></Card>;

  return (
    <div className="flex flex-col gap-5">
      {categories.map((cat) => {
        const list = byCategory[cat.key] || [];
        if (!list.length) return null;
        return (
          <Card key={cat.key} padded={false} className="overflow-hidden">
            <header className="border-b border-border px-5 py-3">
              <h2 className="text-sm font-semibold text-ink">{cat.label}</h2>
            </header>
            <ul className="divide-y divide-border">
              {list.map((ev) => {
                const enabled = ev.rule?.enabled || false;
                return (
                  <li key={ev.key} className="flex items-center gap-4 px-5 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-ink">{ev.label}</span>
                        {enabled && <Badge tone="success">attiva</Badge>}
                      </div>
                      <p className="truncate text-xs text-ink-muted">{ev.description}</p>
                    </div>
                    <button
                      type="button"
                      className="rounded-md p-1.5 text-ink-muted transition-colors hover:bg-surface-subtle hover:text-ink disabled:opacity-40"
                      onClick={() => setEditing(ev)}
                      disabled={!canManage}
                      aria-label="Configura"
                      title="Configura destinatari e template"
                    >
                      <SlidersHorizontal className="h-4 w-4" />
                    </button>
                    <Switch checked={enabled} disabled={!canManage || toggleMutation.isPending} onChange={(v) => toggleMutation.mutate({ key: ev.key, enabled: v })} />
                  </li>
                );
              })}
            </ul>
          </Card>
        );
      })}

      <RuleModal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        event={editing}
        templates={templates}
        roleOptions={roleOptions}
        onSaved={invalidate}
      />
    </div>
  );
}
